import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TooltipDirective } from '@mode/capra';
import { ConfirmDialogService, ToggleSwitchComponent } from '@mode/shared/ui';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { getOwnerUsername } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';

interface OrganizationResource {
  settings: {
    google_sheets_enabled: boolean;
  };
}

@Component({
  standalone: true,
  selector: 'mode-admin-google-sheets-toggle',
  imports: [CommonModule, ToggleSwitchComponent, TooltipDirective],
  templateUrl: './admin-google-sheets-toggle.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminGoogleSheetsToggleComponent implements OnInit {
  @Input() initialGoogleSheetsEnabled = false;

  googleSheetsEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private confirmDialogService: ConfirmDialogService,
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.googleSheetsEnabled$.next(this.initialGoogleSheetsEnabled);
  }

  toggleGoogleSheetsSetting(event) {
    if (this.googleSheetsEnabled$.value) {
      this.confirmDialogService.open({
        dialogTitle: 'Disable the Google Sheets integration?',
        dialogText: `
Disabling the Google Sheets integration will log out any Members currently
connected to Google, and prevent them from re-connecting or creating new
Datasets from Google Sheet data.
<br/><br/>
<span class="bold-text">Existing Datasets built on this connection will continue to use the last
successful sync but won't be able to sync in new data,</span> which could break
existing refresh schedules.
`,
        confirmText: 'Disable',
        confirmAction: () => this.disableGoogleSheets(),
      });
    } else {
      this.enableGoogleSheets();
    }
  }

  private disableGoogleSheets() {
    this.sendApiRequest(false).then((googleSheetsEnabled) => {
      this.googleSheetsEnabled$.next(googleSheetsEnabled);
      if (!googleSheetsEnabled) {
        this.toastService.success('Integration disabled');
      }
    });
  }

  private enableGoogleSheets() {
    this.sendApiRequest(true).then((googleSheetsEnabled) => {
      this.googleSheetsEnabled$.next(googleSheetsEnabled);
    });
  }

  private async sendApiRequest(value: boolean) {
    const orgUserName = getOwnerUsername(window.document);
    const request = `/api/organizations/${orgUserName}`;
    const data = {
      organization: {
        organization_account: {
          google_sheets_enabled: value,
        },
      },
    };

    const { google_sheets_enabled } = (await firstValueFrom(this.http.patch<OrganizationResource>(request, data)))
      .settings;
    return google_sheets_enabled;
  }
}
